'use client';

import { cn } from '../_lib/utils';
import { motion, useMotionValue } from 'motion/react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { Text } from '../Text';

export interface CustomCursorProps {
	className?: string;
}

type CustomCursorState = {
	size: 'small' | 'medium' | 'large' | 'none';
	children: JSX.Element;
};
const customCursorAtom = atom<CustomCursorState>({ size: 'small', children: <></> });

export function useSaveCustomCursor() {
	const setCustomCursor = useSetAtom(customCursorAtom);

	return (newCustomCursor: CustomCursorState) => {
		setCustomCursor(newCustomCursor);
	};
}

export function CustomCursor(props: CustomCursorProps) {
	const [touchScreen, setTouchScreen] = useState(false);
	const customCursor = useAtomValue(customCursorAtom);

	const x = useMotionValue(0);
	const y = useMotionValue(0);

	const cursorRef = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		const isTouchScreen = 'ontouchstart' in window;
		setTouchScreen(isTouchScreen);

		const mouseMoveHandler = (event: any) => {
			if (window.matchMedia && cursorRef.current) {
				const deviceHasMouse = window?.matchMedia('(pointer:fine)').matches;
				const width = cursorRef.current.clientWidth;

				const { clientX, clientY } = event;
				x.set(deviceHasMouse ? clientX - width * 0.52 : 0);
				y.set(deviceHasMouse ? clientY - width * 0.55 : 0);
			}
		};

		document.addEventListener('mousemove', mouseMoveHandler);

		return () => {
			document.removeEventListener('mousemove', mouseMoveHandler);
		};
	}, []);

	return touchScreen ? null : (
		<motion.span
			ref={cursorRef}
			data-component='CustomCursor'
			className={cn(
				`z-9982477 pointer-events-none flex items-center justify-center rounded-full fixed top-0 left-0 text-center h-[6.25rem] w-[6.25rem] px-4 py-4 will-change-transform bg-brand-primary text-brand-secondary`,
				props.className,
			)}
			style={{ x, y }}
			initial={{ scale: 0.05, opacity: 1 }}
			animate={{
				scale: customCursor.size === 'small' ? 0.05 : customCursor.size === 'medium' ? 0.5 : customCursor.size === 'none' ? 0.05 : 1,
				opacity: customCursor.size === 'none' ? 0 : 1,
			}}
			transition={{ duration: 0.3 }}
		>
			<Text as='span' className={`flex flex-col items-center justify-center`} styleType='label-sm'>
				{customCursor.children}
			</Text>
		</motion.span>
	);
}
